import {twMerge} from 'tailwind-merge';
import {
    Button,
    Chip,
    Divider,
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    Textarea,
    Tooltip,
    useDisclosure
} from "@heroui/react";
import React, {useMemo, useState} from 'react';
import VideoContentDeleteButton from './VideoContentDeleteButton.jsx';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import repositories from '../repositories.jsx';
import _ from 'lodash';
import SegmentSpeakers from './SegmentSpeakers.jsx';
import toast from 'react-hot-toast';
import AudioPlayButton from '../components/AudioPlayButton.jsx';
import {
    getSegmentColorWithData,
    parseTimeToMs,
    secondsToTimestampWithMs,
    timestampToSeconds,
    timestampToSecondsMs
} from '../helpers/AppHelper.jsx';
import {useDebouncedCallback} from '@mantine/hooks';
import ConfirmDeductionModal from '../components/ConfirmDeductionModal.jsx';
import {useParams} from 'react-router-dom';
import {v4 as uuidv4} from 'uuid';

const VideoScriptCard = React.forwardRef(({
                                              children,
                                              speakers,
                                              data,
                                              prevData,
                                              segments,
                                              segmentKey,
                                              fps,
                                              formData,
                                              playerRef,
                                              onSelected,
                                              onRemoved,
                                              currentProject,
                                              isSelected = false,
                                              onChange,
                                              isLoading,
                                              ...props
                                          }, ref) => { // Add ref as second parameter
    const {isOpen, onOpen, onOpenChange, onClose} = useDisclosure();
    const onValueChange = useDebouncedCallback(onChange, 500)
    const [isShowCreditModal, setIsShowCreditModal] = useState(false);
    const {ID} = useParams();
    const queryClient = useQueryClient()
    const {isOpen: bufferCreditUseModalIsOpen, onOpenChange: bufferCreditUseModalOnOpenChange} = useDisclosure()


    const segmentTime = timestampToSecondsMs(data?.timestamp?.start);

    const addSegmentsToEditor = async () => {
        if (playerRef.current) {
            await playerRef.current.seekTo(Math.max(segmentTime, 0.001) * fps);
            if (typeof onSelected == 'function') onSelected(segmentKey)
        }
    }

    formData?.watch(`text_srt_json.${segmentKey}.translated_text`, ({value}) => {
        onValueChange('is_audio_changed', value != prevData?.translated_text)
    });

    const onSegmentChange = useMutation({
        mutationFn: () => repositories.project.regenerateSegment(_.merge(data, {
            speaker_id: data.speaker.id,
            is_main_credit_used: formData.values?.is_main_credit_used
        })),
        onSuccess: (res) => {
            const newData = _.merge(data, res)
            onValueChange(null, newData)

            if (res?.voice && _.startsWith(res?.voice, 'http')) {
                onValueChange('voice', res?.voice || data?.voice);
            }

            setTimeout(() => {
                onValueChange('is_original_text_changed', false);
                onValueChange('is_audio_changed', false);
                setTimeout(() => formData.setFieldValue('is_change_available', true), 300)
            }, 200)
            toast.success('Segment updated successfully');
        },
        onError: (error) => {
            toast.error(error?.response?.data?.detail || 'An error occurred while updating segment');
        }
    })

    const onSegmentAudioGenerate = useMutation({
        mutationFn: () => repositories.project.regenerateSegmentAudio({
            ...data,
            speaker: data?.speaker?.name,
            speaker_id: data?.speaker?.id,
            is_main_credit_used: formData.values?.is_main_credit_used,
            str_id: data?.id
        }),
        onSuccess: (res) => {
            if (res?.voice) {
                const newData = _.merge(data, res);
                onValueChange(null, newData);
            }

            if (bufferCreditUseModalIsOpen) {
                bufferCreditUseModalOnOpenChange()
            }

            queryClient.refetchQueries({
                queryKey: ['auth']
            })

            if (res?.current_buffer_credit != null) {
                formData.setFieldValue('current_buffer_credit', res.current_buffer_credit)
            }
        },
        onError: (error) => {
            if (error.response?.data?.error_type === 'confirmation') {
                setIsShowCreditModal(true);
            } else {
                toast.error(error?.response?.data?.detail || 'An error occurred while updating segment');
            }
        }
    });

    const isContentToLength = useMemo(() => {
        const startMs = parseTimeToMs(data?.timestamp?.start || "00:00:00");
        const endMs = parseTimeToMs(data?.timestamp?.end || "00:00:00");
        const audioDuration = parseTimeToMs(data?.audio_duration || "00:00:00");


        // Calculate duration in seconds
        const durationSeconds = Math.max(endMs - startMs, 1000);


        return durationSeconds < audioDuration;
    }, [data?.audio_duration, data?.timestamp])

    const getSegmentBG = useMemo(() => {
        const startMs = timestampToSecondsMs(data?.timestamp?.start || "00:00:00");
        const endMs = timestampToSecondsMs(data?.timestamp?.end || "00:00:00");
        const audioDuration = timestampToSecondsMs(data?.audio_duration || "00:00:00");
        const segmentDuration = _.round(Math.max(endMs - startMs, 1), 2);
        return getSegmentColorWithData(segmentDuration, audioDuration)
    }, [data?.audio_duration, data?.timestamp?.start, data?.timestamp?.end])

    const segmentBetweenDuration = useMemo(() => {
        const getTimestamp = (key, type) => _.get(formData.values, `text_srt_json.${key}.timestamp.${type}`, '00:00:00');

        const beforeSegmentTimeEnd = timestampToSeconds(getTimestamp(segmentKey, 'end'));
        const afterSegmentTimeStart = timestampToSeconds(getTimestamp(segmentKey + 1, 'start'));

        const duration = afterSegmentTimeStart - beforeSegmentTimeEnd;

        return Math.max(duration, 1);
    }, [formData.values, segmentKey]);

    const addedData = () => {
        try {
            const selectedSegment = _.get(formData.values, `text_srt_json.${segmentKey}.timestamp.end`, '00:00:00,000');
            const selectedSegmentTimeEndWithMs = timestampToSecondsMs(selectedSegment);
            const newSegmentStartTime = selectedSegmentTimeEndWithMs + 1;

            const newSegmentEndTime = newSegmentStartTime + 3;

            const newSegment = {
                ...data,
                id: uuidv4(),
                index: data?.index + 1,
                original_text: "",
                translated_text: "",
                timestamp: {
                    start: secondsToTimestampWithMs(newSegmentStartTime),
                    end: secondsToTimestampWithMs(newSegmentEndTime)
                },
                voice: "",
                audio_duration: "00:00:03,000",
                is_audio_changed: false,
                is_text_changed: false,
                is_original_text_changed: false,
                speaker: data?.speaker || null,
                speaker_user: data?.speaker_user || null
            };

            const updatedSegments = formData.values?.text_srt_json || [];
            const beforeSegments = updatedSegments.slice(0, segmentKey + 1);
            const afterSegments = updatedSegments.slice(segmentKey + 1).map(segment => ({
                ...segment,
                index: segment.index + 1
            }));

            return {
                updatedArray: [
                    ...beforeSegments,
                    newSegment,
                    ...afterSegments
                ],
                newSegment
            };

        } catch (error) {
            console.error("Error creating new segment:", error);
            // Return original array if there's an error
            return {
                updatedArray: formData.values?.text_srt_json || [],
                newSegment: null
            };
        }
    };

    return (
        <>
            <ConfirmDeductionModal
                isOpen={isShowCreditModal}
                onClosed={() => setIsShowCreditModal(false)}
                onConfirm={() => {
                    formData.setFieldValue('is_main_credit_used', true)
                    setTimeout(() => onSegmentAudioGenerate.mutate(), 600)
                    setIsShowCreditModal(false)
                }}
            />
            <div
                className="rounded-2xl bg-white"
            >
                <div
                    ref={ref}
                    onClick={addSegmentsToEditor} className={twMerge([
                    "relative col-span-full flex-row pb-3 rounded-2xl flex group grow h-full cursor-pointer transition-all duration-300 py-2 px-2",
                    isSelected ? `bg-${data?.speaker_user}` : 'hover:bg-default/20',
                    props?.className
                ])}>
                    <div
                        className="text-center flex flex-row gap-2 z-20 items-center justify-center absolute left-1/2 -translate-x-1/2 w-full px-3 overflow-hidden">
                        <div className={twMerge([
                            "w-full h-[1px] bg-slate-400 transition-all group-hover:opacity-0",
                            isSelected && "opacity-0"
                        ])}/>
                        <SegmentSpeakers
                            speakers={speakers} data={data}
                            onSelectionChange={(key, value) => {
                                formData.setFieldValue(`text_srt_json.${segmentKey}.${key}`, value)
                                if (key == 'speaker.id') {
                                    onValueChange('is_audio_changed', value != prevData?.speaker?.id)
                                }
                            }}
                        />
                        <Tooltip
                            showArrow color="foreground" delay={600}
                            content={<div
                                className="whitespace-break-spaces text-center"
                                dangerouslySetInnerHTML={{__html: getSegmentBG.tooltip}}
                            />}>
                            <Chip
                                style={{
                                    backgroundColor: `${getSegmentBG?.color}50`
                                }}
                                classNames={{
                                    content: twMerge([
                                        isSelected && "sbg-whitew",
                                        "flex items-center gap-2"
                                    ])
                                }}
                            >
                                <span className="inline-block">
                                    {_.head(_.split(data?.timestamp?.start, ','))} - {_.head(_.split(data?.timestamp?.end, ','))}
                                </span>


                                <span
                                    className="text-sm"
                                    dangerouslySetInnerHTML={{__html: getSegmentBG.icon}}
                                />
                            </Chip>

                        </Tooltip>
                        {(data?.is_audio_changed || (!data?.voice && data?.translated_text?.length > 0)) ?
                            <div className="flex flex-row gap-2 items-center justify-center">
                                <Tooltip
                                    content={`${data?.voice ? "Re-generating this audio" : "Generating this new segment"}  will consume ${formData.values?.tts_charge * data?.translated_text?.length} credits`}>
                                    <Button
                                        isDisabled={(_.size(data?.translated_text) * formData.values?.tts_charge) >= formData?.values?.editing_quota} // Editing Quota
                                        isLoading={onSegmentAudioGenerate.isPending || isLoading}
                                        onPress={() => {
                                            const consumedCredit = formData.values?.tts_charge * data?.translated_text?.length
                                            if (formData.values?.current_buffer_credit > 0 && consumedCredit > formData.values?.current_buffer_credit) {
                                                bufferCreditUseModalOnOpenChange()
                                            } else {
                                                onSegmentAudioGenerate.mutate()
                                            }
                                        }}
                                        className={`min-w-7 min-h-7 h-7 w-7 ${data?.voice ? "text-white" : "text-white font-medium"}`}
                                        color={`${data?.voice ? "secondary" : "success"}`}
                                        variant="solid"
                                        size="sm" isIconOnly radius="full"
                                    >
                                        <i className={twMerge([
                                            "hgi-stroke text-xl",
                                            (data?.is_audio_changed || (!data?.voice && data?.translated_text?.length > 0)) && "hgi-refresh"
                                        ])}></i>
                                    </Button>
                                </Tooltip>
                            </div>
                            :
                            <>
                                <AudioPlayButton
                                    className={`${isSelected ? "bg-white" : "bg-default"} text-dark min-w-7 min-h-7 h-7 w-7`}
                                    src={data?.voice}
                                />
                            </>
                        }
                        <div className={twMerge([
                            "w-full h-[1px] bg-slate-400 transition-all group-hover:opacity-0",
                            isSelected && "opacity-0"
                        ])}/>
                    </div>
                    <div className="relative  w-full">
                        <Textarea
                            aria-label=""
                            label=""
                            isReadOnly
                            placeholder=""
                            classNames={{
                                base: 'pb-2 pt-8',
                                inputWrapper: `shadow-none ${isSelected ? 'bg-transparent hover:!bg-white' : 'hover:bg-amber-200'}`,
                            }}
                            {...formData.getInputProps(`text_srt_json.${segmentKey}.original_text`)}
                            minRows={1}
                            maxRows={2}
                        />
                    </div>
                    <div className="px-2 relative">
                        <Divider orientation="vertical"/>
                        <div>
                            {data?.is_original_text_changed ?
                                <Button
                                    isLoading={onSegmentChange?.isPending}
                                    onPress={onSegmentChange.mutate}
                                    color="primary" variant="solid" size="sm" isIconOnly radius="full"
                                    className="w-full bg-white text-dark !opacity-100 border-0 scale-90 absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-[20%] z-50">
                                    <i className="hgi-stroke hgi-ai-magic text-xl"></i>
                                </Button>
                                : null
                            }
                        </div>
                        <div>
                            {segmentBetweenDuration > 3 ?
                                <Button
                                    onPress={() => {
                                        const { updatedArray, newSegment } = addedData();
                                        if (newSegment) {
                                            onChange(null, {
                                                ...newSegment,
                                                is_new_segment: true
                                            });
                                        }
                                        formData.setFieldValue('text_srt_json', updatedArray)
                                        onValueChange('is_change_available', true)
                                    }}
                                    color="primary" variant="solid" size="sm" isIconOnly radius="full"
                                    className="w-full bg-white text-dark scale-75 opacity-0 group-hover:opacity-100 border-0 absolute left-1/2 -translate-x-1/2 top-1/2 translate-y-[90%] z-50">
                                    <i className="hgi-stroke hgi-add-01 text-2xl"/>
                                </Button>
                                :
                                <Tooltip
                                    delay={500}
                                    color="foreground"
                                    showArrow
                                    classNames={{
                                        content: 'max-w-[400px] p-4 text-center'
                                    }}
                                    content="You need at least 3 seconds of free space to insert a new caption segment. Adjust the timings of this segment or the one following it."
                                >
                                    <Button
                                        color="primary" variant="solid" size="sm" isIconOnly radius="full"
                                        className="w-full bg-warning-100 text-dark scale-75 opacity-0 group-hover:opacity-100 border-0 absolute left-1/2 -translate-x-1/2 top-1/2 translate-y-[90%] z-50">
                                        <i className="hgi-stroke hgi-add-01 text-2xl"/>
                                    </Button>
                                </Tooltip>
                            }
                        </div>
                    </div>
                    <div className="relative w-full">
                        <Textarea
                            aria-label=""
                            label=""
                            placeholder="enter some content"
                            {...formData.getInputProps(`text_srt_json.${segmentKey}.translated_text`)}
                            classNames={{
                                base: 'pb-2 pt-8 ',
                                inputWrapper: `shadow-none ${isSelected ? 'bg-transparent hover:!bg-white' : 'hover:bg-amber-200'}`,
                            }}
                            minRows={1}
                            maxRows={2}
                        />
                    </div>
                    <div className={twMerge([
                        "absolute right-2 top-2 z-50 opacity-0 group-hover:opacity-100 transition-all",
                        isSelected && "opacity-100"
                    ])}>
                        <VideoContentDeleteButton
                            key={data?.translated_text || data?.timestamp?.start} data={data}
                            segmentKey={segmentKey} form={formData}
                            onChange={() => {
                                if (typeof onSelected == 'function') onSelected(segmentKey)
                            }}
                            onRemoved={() => {
                                if (typeof onRemoved == 'function') onRemoved(segmentKey)
                            }}
                        />
                    </div>
                    {isContentToLength &&
                        <div className={twMerge([
                            "absolute right-2 bottom-2 -50 transition-all",
                        ])}>
                            <Tooltip
                                classNames={{
                                    content: 'max-w-72 rounded-xl'
                                }}
                                size={'sm'}
                                delay={500}
                                content={
                                    <div className="flex items-start gap-2 p-1">
                                        <div className="mt-1">
                                            <i className="hgi-stroke hgi-alert-02 w-8 h-8 p-1.5 bg-slate-600 text-white rounded-full"/>
                                        </div>
                                        <div className="text-slate-600">
                                            Content length grows with the timestamp. Please confirm if this is correct.
                                        </div>
                                    </div>
                                }>
                                <Button isIconOnly color="warning" size="sm" variant="faded"
                                        className="bg-transparent border-0 scale-75" radius="full">
                                    <i className="hgi-stroke hgi-alert-02 text-xl"/>
                                </Button>
                            </Tooltip>
                        </div>
                    }
                </div>
            </div>

            <Modal isOpen={bufferCreditUseModalIsOpen} onOpenChange={bufferCreditUseModalOnOpenChange}>
                <ModalContent>
                    {(onClose) => (
                        <>
                            <ModalHeader>
                                <p>Buffer Credit Warning</p>
                            </ModalHeader>
                            <ModalBody>
                                <p>You have used all of your buffer credits. Now, credits will be deducted from your
                                    main credit balance. Would you like to continue?</p>
                            </ModalBody>
                            <ModalFooter>
                                <Button className="bg-[#c8ceda4d]"
                                        onPress={bufferCreditUseModalOnOpenChange}>Cancel</Button>
                                <Button isLoading={onSegmentAudioGenerate.isPending} className="bg-[#7659fa] text-white"
                                        onPress={onSegmentAudioGenerate.mutate}>Confirm</Button>
                            </ModalFooter>
                        </>
                    )}
                </ModalContent>
            </Modal>
        </>
    )
});

VideoScriptCard.displayName = 'VideoScriptCard';

export default VideoScriptCard;